// Stage 44: typed API client for the LIBRARIAN-wiki endpoints. Mirrors the
// wire format defined in stage44-Librarian-wiki-integration-plan.md.

import { apiGet, apiJson } from './client';

export interface WikiProject {
  id: string;
  name: string;
  description: string | null;
  ingest_mode: 'opt_in' | 'opt_out';
  archived: boolean;
  page_count: number;
  source_count: number;
  last_ingest_ts: number | null;
  created_at: number;
}

export interface WikiPageSummary {
  id: string;
  slug: string;
  title: string;
  kind: string;
  version: number;
  updated_at: number;
}

export interface WikiCitation {
  id: string;
  marker: string;
  source_table: string;
  source_id: string;
  plaintext_r2_key: string;
  span_start: number;
  span_end: number;
  claim_text: string;
  quote: string | null;
  verified_similarity: number;
  source_unavailable: boolean;
}

export interface WikiPage {
  id: string;
  slug: string;
  title: string;
  kind: string;
  body: string | null;
  body_unavailable_reason: string | null;
  body_r2_key: string;
  version: number;
  frontmatter: unknown;
  created_at: number;
  updated_at: number;
}

export interface WikiPageBundle {
  page: WikiPage;
  citations: WikiCitation[];
  links: {
    outbound: Array<{ to_slug: string; anchor: string | null }>;
    inbound_count: number;
  };
}

export interface WikiSearchHit {
  kind: 'page' | 'source_span';
  page_id?: string;
  page_slug?: string;
  page_title?: string;
  page_kind?: string;
  source_table?: string;
  source_id?: string;
  source_title?: string;
  span_start?: number;
  span_end?: number;
  snippet?: string;
  score: number;
  supporting?: WikiSearchHit[];
}

export interface WikiSearchResponse {
  query: string;
  mode: 'hybrid' | 'bm25' | 'vector';
  results: WikiSearchHit[];
  took_ms: number;
  degraded: boolean;
}

export interface WikiQueryRequest {
  question: string;
  mode?: 'hybrid' | 'vector' | 'bm25';
  max_pages?: number;
  file_back?: boolean;
}

export interface WikiQueryCitation {
  marker: string;
  source_table: string;
  source_id: string;
  source_title: string;
  span_start: number;
  span_end: number;
  claim_text: string;
  quote: string;
  verified_similarity: number;
}

export interface WikiQueryResponse {
  answer_markdown: string;
  citations: WikiQueryCitation[];
  pages_consulted: Array<{ slug: string; title: string }>;
  filed_as: { slug: string; record_id: string } | null;
  tokens_used: number;
  took_ms: number;
  confidence_signal: 'high' | 'medium' | 'low';
}

export interface WikiLogEntry {
  id: string;
  ts: number;
  kind: string;
  ref: string | null;
  summary: string;
}

export async function listWikiProjects(): Promise<{ projects: WikiProject[] }> {
  return apiGet('/api/wiki/projects');
}

export async function getWikiProject(
  pid: string,
): Promise<{
  project: WikiProject;
  archived: boolean;
  overview: { slug: string; title: string; version: number } | null;
}> {
  return apiGet(`/api/wiki/projects/${encodeURIComponent(pid)}`);
}

export async function listWikiPages(
  pid: string,
  opts?: { limit?: number; offset?: number; kinds?: string[] },
): Promise<{ pages: WikiPageSummary[] }> {
  const qs = new URLSearchParams();
  if (opts?.limit) qs.set('limit', String(opts.limit));
  if (opts?.offset) qs.set('offset', String(opts.offset));
  if (opts?.kinds?.length) qs.set('kinds', opts.kinds.join(','));
  const tail = qs.toString();
  return apiGet(
    `/api/wiki/projects/${encodeURIComponent(pid)}/pages${tail ? '?' + tail : ''}`,
  );
}

export async function getWikiPage(
  pid: string,
  slug: string,
): Promise<WikiPageBundle> {
  return apiGet(
    `/api/wiki/projects/${encodeURIComponent(pid)}/pages/${slug}`,
  );
}

export async function getWikiBacklinks(
  pid: string,
  slug: string,
): Promise<{
  backlinks: Array<{
    from_slug: string;
    from_title: string;
    anchor: string | null;
  }>;
}> {
  return apiGet(
    `/api/wiki/projects/${encodeURIComponent(pid)}/pages/${slug}/backlinks`,
  );
}

export async function wikiSearch(
  pid: string,
  q: string,
  mode: 'hybrid' | 'bm25' | 'vector' = 'hybrid',
  limit = 10,
): Promise<WikiSearchResponse> {
  const qs = new URLSearchParams();
  qs.set('q', q);
  qs.set('mode', mode);
  qs.set('limit', String(limit));
  return apiGet(
    `/api/wiki/projects/${encodeURIComponent(pid)}/search?${qs.toString()}`,
  );
}

export async function wikiQuery(
  pid: string,
  body: WikiQueryRequest,
): Promise<WikiQueryResponse> {
  return apiJson(
    'POST',
    `/api/wiki/projects/${encodeURIComponent(pid)}/query`,
    body,
  );
}

export async function wikiLog(
  pid: string,
  opts?: { since?: number; limit?: number; kinds?: string[] },
): Promise<{ log: WikiLogEntry[] }> {
  const qs = new URLSearchParams();
  if (opts?.since) qs.set('since', String(opts.since));
  if (opts?.limit) qs.set('limit', String(opts.limit));
  if (opts?.kinds?.length) qs.set('kinds', opts.kinds.join(','));
  const tail = qs.toString();
  return apiGet(
    `/api/wiki/projects/${encodeURIComponent(pid)}/log${tail ? '?' + tail : ''}`,
  );
}

export async function wikifySource(
  pid: string,
  source_table: string,
  source_id: string,
): Promise<{ ok: boolean }> {
  return apiJson(
    'POST',
    `/api/wiki/projects/${encodeURIComponent(pid)}/wikify/${source_table}/${source_id}`,
    {},
  );
}

export async function skipSource(
  pid: string,
  source_table: string,
  source_id: string,
): Promise<{ ok: boolean }> {
  return apiJson(
    'POST',
    `/api/wiki/projects/${encodeURIComponent(pid)}/skip/${source_table}/${source_id}`,
    {},
  );
}

export async function reingestSource(
  pid: string,
  source_table: string,
  source_id: string,
): Promise<{ ok: boolean }> {
  return apiJson(
    'POST',
    `/api/wiki/projects/${encodeURIComponent(pid)}/reingest/${source_table}/${source_id}`,
    {},
  );
}

export async function archiveProject(pid: string): Promise<{ ok: boolean }> {
  return apiJson('DELETE', `/api/wiki/projects/${encodeURIComponent(pid)}`);
}
